import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  useTheme,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Tooltip,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { UPDATE_AUDIT } from "../../../../apis/settingsApis/audit-apis/auditApis";
import CreateAuditDialog from "../components/Dialogs/CreateAuditDialog";
import EditAuditDialog from "../components/Dialogs/EditAuditDialog";
import ConfirmSnackbar from "../../../../components/Snackbar/ConfirmSnackbar";
import InformationStepsCard from "../../../../components/InformationStepsCard";
import AuditSummary from "../components/common/AuditSummary";
import { AuditStatus } from "../../../../utils/enums";
import { useAuditList } from "../hooks/useAuditList";
import { useAuditListFilters } from "../hooks/useAuditListFilters";
import AuditListTable from "../features/audit-list/AuditListTable";

const AuditList = ({ isAuditorView = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filterAuditType, setFilterAuditType] = useState("");
  const [filterFramework, setFilterFramework] = useState("");
  const [filterStatus, setFilterStatus] = useState("active");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [archivingAuditId, setArchivingAuditId] = useState(null);
  
  const [updateAudit] = useMutation(UPDATE_AUDIT);

  const { audits, frameworks, processedAudits, loading, refetch } = useAuditList({
    filterAuditType,
    filterFramework,
  });
      
  const { filteredAudits, activeFiltersCount, hasActiveFilters } = useAuditListFilters({
    processedAudits,
    searchQuery,
    filterStatus,
    filterAuditType,
    filterFramework,
  });


  const clearFilters = () => {
    setFilterAuditType("");
    setFilterFramework("");
  };

  const removeFilter = (filterType) => {
    if (filterType === "audit_type") {
      setFilterAuditType("");
    } else if (filterType === "framework") {
      setFilterFramework("");
    }
  };

  const handleArchiveAudit = async (audit) => {
    setArchivingAuditId(audit.id);
    try {
      const newStatus = audit.audit_status === AuditStatus.ARCHIVED 
        ? AuditStatus.ACTIVE 
        : AuditStatus.ARCHIVED;
      
      await updateAudit({
        variables: {
          input: [{
            id: audit.id,
            audit_status: newStatus,
          }],
        },
      });

      setSnackbarInfo({
        message: newStatus === AuditStatus.ARCHIVED
          ? "Audit archived successfully!"
          : "Audit unarchived successfully!",
        vertical: "top",
        horizontal: "right",
      });
      
      await refetch();
    } catch (error) {
      console.error("Archive error:", error);
      setSnackbarInfo({
        message: "Failed to archive/unarchive audit. Please try again.",
        vertical: "top",
        horizontal: "right",
        severity: "error",
      });
    } finally {
      setArchivingAuditId(null);
    }
  };

  const handleViewDetails = (audit) => {
    navigate(`${isAuditorView ? '/auditor' : '/admin'}/audits/${audit.id}`);
  };

  return (
    <Box sx={{ width: "100%", typography: "body1" }}>
      {/* Header like People page */}

      {/* Information Card */}
      <Box sx={{ mb: 3 }}>
        <InformationStepsCard
          heading="**What should I do on this page?**"
          subtitle="This page helps you manage and track audits for your compliance frameworks."
          steps={[
            "View all audits with their associated **frameworks**, **audit types**, and assigned **auditors**.",
            "Create new audits by clicking the **'Add Audit'** button and filling in the required details.",
            "Use **filters** to quickly find audits by type, framework, or status.",
            "**Search** for audits by framework name, audit type, or auditor name.",
            "**View details** of any audit by clicking the view details icon in the Actions column.",
            "Monitor **audit status** to track which audits are currently in progress."
          ]}
          footerText="💡 Regular audit management helps ensure **compliance** with regulatory requirements and maintains your organization's **audit readiness**."
          collapsible={true}
          defaultExpanded={false}
        />
      </Box>

      {/* Audit Summary Cards */}
      <AuditSummary audits={audits} loading={loading} />

      {/* Main Card */}
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #E8EAED",
        }}
        variant="outlined"
      >
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Typography variant="h5">
                Audits Management
              </Typography>
              <ToggleButtonGroup
                value={filterStatus}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null) {
                    setFilterStatus(newValue);
                  }
                }}
                aria-label="status filter"
                size="small"
                sx={{
                  borderRadius: '26px',
                  border: '1px solid #E8EAED',
                  gap: 0.2,
                  overflow: 'hidden',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    borderRadius: '36px',
                    color: '#6B7280',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    px: 3,
                    py: 1,
                    minWidth: '70px',
                    transition: 'all 0.2s ease-in-out',
                    '&.Mui-selected': {
                      backgroundColor: '#3B82F6',
                      color: '#fff',
                      boxShadow: 'inset 0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      '&:hover': {
                        backgroundColor: '#2563EB',
                      },
                    },
                    '&:hover:not(.Mui-selected)': {
                      backgroundColor: '#F9FAFB',
                      color: '#374151',
                    },
                  },
                }}
              >
                <ToggleButton value="active">Active</ToggleButton>
                <ToggleButton value="completed">Completed</ToggleButton>
                <ToggleButton value="archived">Archived</ToggleButton>
              </ToggleButtonGroup>
              {hasActiveFilters && (
                <Chip
                  label={`${activeFiltersCount} filter${activeFiltersCount !== 1 ? "s" : ""} active`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    height: 24,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: theme.customColors.text.secondary,
                    borderColor: theme.customColors.border.main,
                    background: theme.customColors.background.light,
                  }}
                />
              )}
            </Box>
          }
          sx={{
            borderBottom: "1px solid #ddd",
            background: "#F7F7FC",
            ".MuiCardHeader-title": {
              fontSize: "16px",
              fontWeight: "bold",
            },
            padding: "14px",
          }}
          action={
            !isAuditorView && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Tooltip title="Add Audit" arrow>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setCreateDialogOpen(true)}
                    sx={{
                      bgcolor: theme.customColors.button.primary,
                      color: theme.customColors.button.text,
                      fontWeight: 500,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "14px",
                      px: 2,
                      py: 1,
                      "&:hover": {
                        bgcolor: theme.palette._blue?.dark || theme.customColors.button.primary,
                      },
                    }}
                  >
                    Add Audit
                  </Button>
                </span>
              </Tooltip>
            </Box>
            )
          }
        />

        <CardContent sx={{ p: 0 }}>
          {/* Search and Filters Section */}
          <Box sx={{ p: 2, pb: 1.5, backgroundColor: theme.customColors.background.main }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: showFilters ? 2 : 0 }}>
              <TextField
                fullWidth
                placeholder="Search audits..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery("")}
                        edge="end"
                      >
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "white",
                  },
                }}
              />

              <Button
                variant={hasActiveFilters ? "contained" : "outlined"}
                startIcon={<FilterListIcon />}
                endIcon={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      transform: showFilters ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.3s",
                    }}
                  >
                    <ExpandMoreIcon sx={{ fontSize: 18 }} />
                  </Box>
                }
                onClick={() => setShowFilters(!showFilters)}
                sx={{
                  minWidth: 140,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  ...(hasActiveFilters && {
                    bgcolor: "#3B82F6",
                    color: "#fff",
                    "&:hover": {
                      bgcolor: "#2563EB",
                    },
                  }),
                  ...(!hasActiveFilters && {
                    borderColor: "#3B82F6",
                    color: "#3B82F6",
                    "&:hover": {
                      borderColor: "#2563EB",
                      bgcolor: "rgba(59, 130, 246, 0.08)",
                    },
                  }),
                }}
              >
                Filters
                {hasActiveFilters && (
                  <Chip
                    label={activeFiltersCount}
                    size="small"
                    sx={{
                      ml: 1,
                      height: 20,
                      minWidth: 20,
                      bgcolor: "white",
                      color: "#3B82F6",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      "& .MuiChip-label": {
                        px: 0.75,
                      },
                    }}
                  />
                )}
              </Button>
            </Box>

            {/* Collapsible Filter Panel */}
            <Collapse in={showFilters}>
              <Box
                sx={{
                  p: 2.5,
                  backgroundColor: "#F9FAFB",
                  borderRadius: "8px",
                  border: "1px solid #E5E7EB",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(2, 1fr)" },
                    gap: 2,
                  }}
                >
                  <FormControl fullWidth size="small">
                    <InputLabel>Audit Type</InputLabel>
                    <Select
                      value={filterAuditType}
                      onChange={(e) => setFilterAuditType(e.target.value)}
                      label="Audit Type"
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: "white",
                      }}
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="internal">Internal</MenuItem>
                      <MenuItem value="external">External</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>Framework</InputLabel>
                    <Select
                      value={filterFramework}
                      onChange={(e) => setFilterFramework(e.target.value)}
                      label="Framework"
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: "white",
                      }}
                    >
                      <MenuItem value="">All Frameworks</MenuItem>
                      {frameworks
                        .filter((f) => f.is_active)
                        .map((framework) => (
                          <MenuItem key={framework.framework_id} value={framework.framework_id}>
                            {framework.framework_name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>

                {hasActiveFilters && (
                  <Box
                    sx={{
                      mt: 2,
                      pt: 1.5,
                      borderTop: "1px solid #E5E7EB",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      size="small"
                      onClick={clearFilters}
                      startIcon={<ClearIcon />}
                      sx={{
                        textTransform: "none",
                        color: "#3B82F6",
                        fontWeight: 500,
                        "&:hover": {
                          bgcolor: "rgba(59, 130, 246, 0.08)",
                        },
                      }}
                    >
                      Clear all filters
                    </Button>

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                      {filterAuditType && (
                        <Chip
                          label={`Type: ${filterAuditType}`}
                          onDelete={() => removeFilter("audit_type")}
                          size="small"
                          sx={{
                            backgroundColor: "#3B82F6",
                            color: "#fff",
                            border: "2px solid #3B82F6",
                            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            height: "32px",
                            borderRadius: "12px",
                            "& .MuiChip-deleteIcon": {
                              color: "#fff",
                              "&:hover": {
                                color: "#E5E7EB",
                              },
                            },
                          }}
                        />
                      )}
                      {filterFramework && (
                        <Chip
                          label={`Framework: ${frameworks.find((f) => f.framework_id === parseInt(filterFramework))?.framework_name || filterFramework}`}
                          onDelete={() => removeFilter("framework")}
                          size="small"
                          sx={{
                            backgroundColor: "#3B82F6",
                            color: "#fff",
                            border: "2px solid #3B82F6",
                            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            height: "32px",
                            borderRadius: "12px",
                            "& .MuiChip-deleteIcon": {
                              color: "#fff",
                              "&:hover": {
                                color: "#E5E7EB",
                              },
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>

          <Divider sx={{ borderColor: theme.customColors.border.main }} />

          {/* Count and Table */}
          <Box sx={{ px: 2, py: 1.5, mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: "0.875rem" }}>
              {filteredAudits.length} audit{filteredAudits.length !== 1 ? "s" : ""} found
            </Typography>
          </Box>

          <Box>
            <AuditListTable
              audits={filteredAudits}
              loading={loading}
              isAuditorView={isAuditorView}
              onArchiveAudit={handleArchiveAudit}
              onViewDetails={handleViewDetails}
              archivingAuditId={archivingAuditId}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <CreateAuditDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        setSnackbarInfo={setSnackbarInfo}
        refetchAudits={refetch}
      />

      {/* Edit Dialog */}
      {selectedAudit && (
        <EditAuditDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedAudit(null);
          }}
          setSnackbarInfo={setSnackbarInfo}
          refetchAudits={refetch}
          audit={selectedAudit}
          navigate={navigate}
          isAuditorView={isAuditorView}
        />
      )}

      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </Box>
  );
};

export default AuditList;
